import { 
  GET_ACTIVE_PROGRAMS_DATA_SUCCESS, 
  EMPTY_ACTIVE_PROGRAMS_EXPORT_DATA_UPDATE,
  FETCH_ACTIVE_PROGRAMS_DATA_REQUEST,
  GET_ACTIVE_PROGRAMS_DATA_FAILURE,
  VBC_FILTER_TIME_PERIOD_UPDATE,
  VBC_FILTER_LOB_UPDATE,
  VBC_FILTER_TAXIDS_UPDATE,
  VBC_TAXIDS_REQUEST,
  VBC_TAXIDS_LOADING,
  VBC_TAXIDS_SUCCESS,
  VBC_TAXIDS_FAILURE,
  VBC_GEOFILTER_REGION,
  VBC_GEOFILTER_STATE,
  VBC_GEOFILTER_MARKET,
  GET_PAYOUTS_DATA_SUCCESS,
  FETCH_PAYOUTS_DATA_REQUEST,
  GET_PAYOUTS_DATA_FAILURE,
  GET_INFLATORS_DATA_SUCCESS,
  FETCH_INFLATORS_DATA_REQUEST,
  GET_INFLATORS_DATA_FAILURE
} from "../../../actions/actionTypes";
import { formatCurrencyWithKSuffix, formatVBCDate, getSELob, getInitialCalendarYear } from "@src/utility/commonUtilityServices";

interface ValueBasedContractingState {
  activeProgramsData: [];
  exportData: [];
  payoutsData: [];
  payoutsSummary: {
    totalPayouts: string;
    totalMedicare: string;
    totalCommercial: string;
    totalMedicaid: string;
  };
  inflatorsData: [];
  loading: boolean;
  error: any;
  activeProgramsLoading: boolean;
  payoutsLoading: boolean;
  inflatorsLoading: boolean;
  inflatorsError: { statusCode?: number } | null;
  timePeriodVBC: string;
  lob: string[];
  taxIds: string[];
  taxIdsLoading: boolean;
  taxIdsError: any;
  taxIdsOptions: any[];
  geoRegion: any;
  geoState: any;
  geoMarket: any;
}



const initialState: ValueBasedContractingState = {
  activeProgramsData: [],
  exportData: [],
  payoutsData: [],
  payoutsSummary: {
    totalPayouts: '$0',
    totalMedicare: '$0',
    totalCommercial: '$0',
    totalMedicaid: '$0'
  },
  inflatorsData: [],
  loading: false,
  error: null,
  activeProgramsLoading: false,
  payoutsLoading: false,
  inflatorsLoading: false,
  inflatorsError: null,
  timePeriodVBC: getInitialCalendarYear(),
  lob: [],
  taxIds: [],
  taxIdsLoading: false,
  taxIdsError: null,
  taxIdsOptions: [],
  // geo segmentation filters initial state
  geoRegion: '',
  geoState: '',
  geoMarket: [],
};





const valueBasedContractingReducer = (
  state = initialState,
  action: { type: string; payload?: any }
): ValueBasedContractingState => {
  switch (action.type) {
    case FETCH_ACTIVE_PROGRAMS_DATA_REQUEST:
      return {
        ...state,
        activeProgramsLoading: true,
        error: null,
        loading: true,
      };
    case GET_ACTIVE_PROGRAMS_DATA_SUCCESS: {
      const formattedActiveProgramsData = (action.payload?.Data ?? []).map((program: any) => ({
        ...program,
        Lob: getSELob(program.Lob)
      }));

      return {
        ...state,
        activeProgramsData: formattedActiveProgramsData,
        activeProgramsLoading: false,
        error: null,
        loading: false,
      };
    }
    case GET_ACTIVE_PROGRAMS_DATA_FAILURE:
      return {
        ...state,
        activeProgramsLoading: false,
        error: action.payload,
        loading: false,
      };
    case VBC_FILTER_TIME_PERIOD_UPDATE:
      return {
        ...state,
        timePeriodVBC: action.payload,
      };
    case VBC_FILTER_LOB_UPDATE:
      return {
        ...state,
        lob: [...(action.payload || [])],
      };
    case VBC_FILTER_TAXIDS_UPDATE:
      return {
        ...state,
        taxIds: action.payload,
      };
    case VBC_TAXIDS_REQUEST:
      return {
        ...state,
        taxIdsLoading: true,
        taxIdsError: null,
      };
    case VBC_TAXIDS_LOADING:
      return {
        ...state,
        taxIdsLoading: action.payload,
      };
    case VBC_TAXIDS_SUCCESS:
      return {
        ...state,
        taxIdsOptions: action.payload,
        taxIdsLoading: false,
        taxIdsError: null,
      };
    case VBC_TAXIDS_FAILURE:
      return {
        ...state,
        taxIdsLoading: false,
        taxIdsError: action.payload,
        taxIdsOptions: [],
      };
    case EMPTY_ACTIVE_PROGRAMS_EXPORT_DATA_UPDATE:
      return {
        ...state,
        exportData: [],
      };
    case FETCH_PAYOUTS_DATA_REQUEST:
      return {
        ...state,
        payoutsLoading: true,
        error: null,
        loading: true,
      };
    case GET_PAYOUTS_DATA_SUCCESS: {
      // Payouts API returns Data: {Programs: Array, TotalPayoutAmount: number}
      const payoutsPrograms = action.payload?.Data?.Programs ?? [];
      const totalPayoutAmount = action.payload?.Data?.TotalPayoutAmount ?? 0;

      const formattedPayoutsPrograms = payoutsPrograms.map((program: any) => ({
        ...program,
        PayeeTaxIdName: program.Tin && program.TinName ? `${program.Tin}\n${program.TinName}` : '',
        Lob: getSELob(program.Lob),
        PayoutAmount: formatCurrencyWithKSuffix(program.PayoutAmount),
        PayoutDate: formatVBCDate(program.PayoutDate)
      }));
      
      const lobTotals = payoutsPrograms.reduce((totals: any, program: any) => {
        const amount = parseFloat(program.PayoutAmount || 0);
        const lob = program.Lob;
        
        if (lob === 'M&R') {
          totals.medicare += amount;
        } else if (lob === 'C&S') {
          totals.medicaid += amount;
        } else if (lob === 'E&I') {
          totals.commercial += amount;
        }
        
        return totals;
      }, { medicaid: 0, medicare: 0, commercial: 0 });
      
      // Helper function to format summary amounts with K/M suffix
      const formatSummaryAmount = (amount: number) => {
        if (amount >= 1000000) {
          // Millions: show two digits before and two after decimal (e.g., $485.50M)
          const mValue = amount / 1000000;
          // Always show two digits before and after decimal
          const formatted = mValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          return `$${formatted}M`;
        } else if (amount >= 1000) {
          // Thousands: show one decimal point (e.g., $45.2K)
          const kValue = amount / 1000;
          const formatted = kValue.toFixed(1);
          const trimmed = formatted.replace(/\.0$/, '');
          return `$${trimmed}K`;
        } else {
          // Less than 1K: show two decimal points
          return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        }
      };

      const calculatedSummary = {
        totalPayouts: formatSummaryAmount(totalPayoutAmount),
        totalMedicare: formatSummaryAmount(lobTotals.medicare),
        totalCommercial: formatSummaryAmount(lobTotals.commercial),
        totalMedicaid: formatSummaryAmount(lobTotals.medicaid),
      };

      return {
        ...state,
        payoutsData: formattedPayoutsPrograms,
        payoutsSummary: calculatedSummary,
        payoutsLoading: false,
        error: null,
        loading: false,
      };
    }
    case GET_PAYOUTS_DATA_FAILURE:
      return {
        ...state,
        payoutsLoading: false,
        error: action.payload,
        loading: false,
      };
    case FETCH_INFLATORS_DATA_REQUEST:
      return {
        ...state,
        inflatorsLoading: true,
        inflatorsError: null,
        error: null,
        loading: true,
      };
    case GET_INFLATORS_DATA_SUCCESS: {
      const inflatorsPrograms = action.payload?.Data ?? [];
      const formattedInflatorsData = inflatorsPrograms.map((program: Record<string, any>) => ({
        ...program,
        TaxIdName: program.TaxId && program.TaxIdName ? `${program.TaxId}\n${program.TaxIdName}` : program.TaxIdName || program.TaxId || '',
        Lob: getSELob(program.Lob),
        EarnedInflator: program.EarnedInflator,
        MaximumInflator: program.MaximumInflator,
        EstimatedOpportunities: formatCurrencyWithKSuffix(program.EstimatedOpportunities),
        InflatorEndDate: program.Year
      }));

      return {
        ...state,
        inflatorsData: formattedInflatorsData,
        inflatorsLoading: false,
        inflatorsError: null,
        error: null,
        loading: false,
      };
    }
    case GET_INFLATORS_DATA_FAILURE:
      return {
        ...state,
        inflatorsLoading: false,
        inflatorsError: action.payload,
        error: action.payload,
        loading: false,
      };
    case VBC_GEOFILTER_REGION:
      return {
        ...state,
        geoRegion: action.payload,
      };
    case VBC_GEOFILTER_STATE:
      return {
        ...state,
        geoState: action.payload,
      };
    case VBC_GEOFILTER_MARKET:
      return {
        ...state,
        geoMarket: action.payload,
      };
    default:
      return state;
    }
};

export default valueBasedContractingReducer;